<template>
    <div class="modern-form-container">
        <div class="form-header">
            <div class="header-content">
                <div class="title-section">
                    <div class="icon-wrapper">
                        <i class="fas fa-prescription-bottle-alt"></i>
                    </div>
                    <div>
                        <h4 class="form-title">
                            {{ localNoteForm.id ? "Editar Receta Médica" : "Nueva Receta Médica" }}
                            <span v-if="localNoteForm.id" class="id-badge">#{{ localNoteForm.id }}</span>
                        </h4>
                        <p class="form-subtitle">Prescripción de medicamentos e indicaciones médicas</p>
                    </div>
                </div>
                <div class="action-buttons">
                    <ArgonButton
variant="gradient" color="success" :disabled="isSaving" size="md" button-type="submit"
                        class="header-btn-primary" @click="handleSubmit">
                        <i :class="localNoteForm.id ? 'fas fa-save' : 'fas fa-plus-circle'" class="me-2"></i>
                        {{ isSaving ? 'Guardando...' : (localNoteForm.id ? 'Guardar Receta' : 'Crear Receta') }}
                    </ArgonButton>
                    <div v-if="isEditing" class="secondary-actions">
                        <ArgonButton
variant="gradient" color="warning" size="sm" class="header-btn-secondary"
                            data-bs-toggle="tooltip" title="Generar PDF" @click="generatePDF">
                            <i class="fas fa-file-pdf"></i>
                        </ArgonButton>
                        <ArgonButton
variant="gradient" color="success" size="sm" class="header-btn-secondary"
                            data-bs-toggle="tooltip" title="Enviar por WhatsApp" :disabled="isSendingWhatsApp" @click="sendWhatsApp">
                            <i :class="isSendingWhatsApp ? 'fas fa-spinner fa-spin' : 'fab fa-whatsapp'"></i>
                        </ArgonButton>
                        <ArgonButton
variant="gradient" color="info" size="sm" class="header-btn-secondary"
                            data-bs-toggle="tooltip" title="Duplicar receta" @click="handleDuplicateNote">
                            <i class="fas fa-copy"></i>
                        </ArgonButton>
                        <ArgonButton
variant="gradient" color="danger" size="sm" class="header-btn-secondary"
                            data-bs-toggle="tooltip" title="Eliminar receta" @click="deleteNote('medicines')">
                            <i class="fas fa-trash"></i>
                        </ArgonButton>
                    </div>
                </div>
            </div>
        </div>

        <!-- Barra de estado profesional -->
        <div class="status-bar">
            <div class="status-left">
                <ProgressIndicator :form-data="localNoteForm" :compact="true" />
            </div>

            <div class="status-center">
                <!-- Notificación de borrador inline -->
                <transition name="slide-fade">
                    <div v-if="hasDraft" class="draft-badge">
                        <i class="fas fa-file-alt"></i>
                        <span>Borrador disponible</span>
                        <button class="draft-btn-restore" title="Restaurar borrador" @click="handleRestoreDraft">
                            <i class="fas fa-undo"></i>
                        </button>
                        <button class="draft-btn-discard" title="Descartar borrador" @click="handleDiscardDraft">
                            <i class="fas fa-times"></i>
                        </button>
                    </div>
                </transition>
            </div>

            <div class="status-right">
                <MedicineSelectorModal
:label="'Medicamentos'" :can-edit-and-add="true"
                    @medicine-selected="addMedicineToEditor">
                    <template #trigger="{ open }">
                        <ArgonButton variant="gradient" color="success" size="sm" @click="open">
                            <i class="fas fa-pills me-1"></i>
                            Medicamentos
                        </ArgonButton>
                    </template>
                </MedicineSelectorModal>

                <HTMLSelectorModal
:label="'Plantillas'" :can-edit-and-add="true"
                    :editor-content="localNoteForm.medicines_txt || ''" @template-selected="addTemplateContentToEditor"
                    @update:editor-content="updateEditorContent">
                    <template #trigger="{ open }">
                        <ArgonButton variant="gradient" color="info" size="sm" @click="open">
                            <i class="fas fa-file-code me-1"></i>
                            Plantillas
                        </ArgonButton>
                    </template>
                </HTMLSelectorModal>

                <AutosaveIndicator
:last-save-time="lastSaveTime" :has-unsaved-changes="hasUnsavedChanges"
                    :has-draft="hasDraft" :formatted-save-time="getFormattedSaveTime()" :compact="true" :inline="true"
                    @restore-draft="handleRestoreDraft" @discard-draft="handleDiscardDraft" />
            </div>
        </div>

        <form class="modern-form" @submit.prevent="handleSubmit">
            <!-- Sección Principal: Medicamentos -->
            <div class="form-section">
                <div class="section-title enhanced">
                    <div class="title-left">
                        <i class="fas fa-pills"></i>
                        <span> Medicamentos Prescritos</span>
                        <span class="required-badge">Requerido</span>
                    </div>
                </div>

                <div class="form-group enhanced">
                    <label for="medicines" class="form-label modern">
                        <span class="label-content">
                            <i class="fas fa-prescription"></i>
                            <span>Listado de medicamentos con dosis y frecuencia</span>
                        </span>
                    </label>
                    <div class="editor-wrapper" :class="{ 'has-error': errors.medicines_txt }">
                        <quill-editor
ref="medicineEditorRef" v-model:value="localNoteForm.medicines_txt"
                            :options="$editorOptions" class="editor modern" @focus="activeEditor = 'medicines_txt'"
                            @blur="activeEditor = null" />
                        <div class="editor-footer">
                            <div class="editor-stats">
                                <span class="stat-item">
                                    <i class="fas fa-font"></i>
                                    {{ getTextLength(localNoteForm.medicines_txt) }} caracteres
                                </span>
                                <span class="stat-item">
                                    <i class="fas fa-align-left"></i>
                                    {{ getWordCount(localNoteForm.medicines_txt) }} palabras
                                </span>
                            </div>
                        </div>
                    </div>
                    <transition name="error-slide-down">
                        <div v-if="errors.medicines_txt" class="error-message-modern">
                            <i class="fas fa-exclamation-triangle"></i>
                            <span>{{ getErrorMessage(errors.medicines_txt) }}</span>
                        </div>
                    </transition>
                    <div v-if="!errors.medicines_txt && !localNoteForm.medicines_txt" class="help-text-modern">
                        <i class="fas fa-lightbulb"></i>
                        <span>Ejemplo: Paracetamol 500mg, 1 tableta cada 8 horas por 5 días</span>
                    </div>
                </div>
            </div>

            <!-- Sección Indicaciones -->
            <div class="form-section">
                <div class="section-title enhanced">
                    <div class="title-left">
                        <i class="fas fa-clipboard-list"></i>
                        <span> Indicaciones Médicas</span>
                    </div>
                </div>

                <div class="form-group enhanced">
                    <label for="indications" class="form-label modern">
                        <span class="label-content">
                            <i class="fas fa-instructions"></i>
                            <span>Instrucciones para el paciente</span>
                        </span>
                    </label>
                    <div class="editor-wrapper" :class="{ 'has-error': errors.indications_txt }">
                        <quill-editor
ref="indicationsEditorRef" v-model:value="localNoteForm.indications_txt"
                            :options="$editorOptions" class="editor modern" @focus="activeEditor = 'indications_txt'"
                            @blur="activeEditor = null" />
                        <div class="editor-footer">
                            <div class="editor-stats">
                                <span class="stat-item">
                                    <i class="fas fa-font"></i>
                                    {{ getTextLength(localNoteForm.indications_txt) }} caracteres
                                </span>
                            </div>
                        </div>
                    </div>
                    <transition name="error-slide-down">
                        <div v-if="errors.indications_txt" class="error-message-modern">
                            <i class="fas fa-exclamation-triangle"></i>
                            <span>{{ getErrorMessage(errors.indications_txt) }}</span>
                        </div>
                    </transition>
                    <div v-if="!errors.indications_txt && !localNoteForm.indications_txt" class="help-text-modern">
                        <i class="fas fa-lightbulb"></i>
                        <span>Incluye recomendaciones de dieta, reposo o cuidados especiales</span>
                    </div>
                </div>
            </div>

            <!-- Sección Exploración Física -->
            <div class="form-section collapsed-section">
                <div class="section-title collapsible" @click="togglePhysicalExam">
                    <i class="fas fa-heartbeat"></i>
                    <span>Exploración Física</span>
                    <i class="fas fa-chevron-down toggle-icon" :class="{ 'rotated': showPhysicalExam }"></i>
                </div>

                <div v-show="showPhysicalExam" class="collapsible-content">
                    <PhysicalExaminationForm v-model="localNoteForm.physical_examination_json" />
                </div>
            </div>

            <!-- Sección Diagnóstico y Citas -->
            <div class="form-section">
                <div class="section-title">
                    <i class="fas fa-calendar-check"></i>
                    <span>Diagnóstico y Seguimiento</span>
                </div>

                <!-- Diagnóstico principal en una sola fila -->
                <div class="diagnosis-full">
                    <label class="form-label modern">
                        <span class="label-content">
                            <i class="fas fa-star"></i>
                            <span>Diagnóstico CIE-10 Principal</span>
                        </span>
                    </label>
                    <MenuSelectedFull
v-model="localNoteForm.diagnosis_cie10_1" :label="''" apiurl="cie10"
                        :can-edit-and-add="false" />
                </div>

                <!-- Fechas en una fila compacta -->
                <div class="dates-row">
                    <div class="date-item">
                        <label class="form-label modern">
                            <span class="label-content">
                                <i class="fas fa-calendar-plus"></i>
                                <span>Próxima cita</span>
                            </span>
                        </label>
                        <DatePicker
v-model="localNoteForm.appointment" :errors="errors"
                            error-field-name="appointment" />
                    </div>

                    <div class="date-item">
                        <label class="form-label modern">
                            <span class="label-content">
                                <i class="fas fa-calendar-alt"></i>
                                <span>Fecha de creación</span>
                            </span>
                        </label>
                        <DatePicker v-model="localNoteForm.created" :errors="errors" error-field-name="created" />
                    </div>
                </div>
            </div>

            <!-- Navegación entre recetas (solo en modo editar) -->
            <div v-if="isEditing && filteredNotes.length > 0" class="notes-navigation">
                <NotesFiltersButtons
:filtered-notes="filteredNotes" :current-id="currentId"
                    :select-note="selectNoteForm" :format-date="formatDate" record-type="receta" />
            </div>
        </form>

        <!-- Ayuda de atajos de teclado -->
        <KeyboardShortcutsHelp ref="keyboardHelpRef" :is-mac="isMac" />
    </div>
</template>

<script setup>
import { ref, watch, onMounted, nextTick, getCurrentInstance, computed } from 'vue';
import { useNoteFormGlobal } from '@/composables/useNoteFormGlobal';
import { useKeyboardShortcuts } from '@/composables/useKeyboardShortcuts';
import { useAutosave } from '@/composables/useAutosave';
// ✅ Imports absolutos corregidos
import NotesFiltersButtons from '@/components/NotesFiltersButtons.vue';
import DatePicker from "@/components/DatePicker.vue";
import PhysicalExaminationForm from '@/components/PhysicalExaminationForm.vue';
import MenuSelectedFull from '@/components/MenuSelectedFull.vue';
import MedicineSelectorModal from '@/components/MedicineSelectorModal.vue';
import HTMLSelectorModal from '@/components/HTMLSelectorModal.vue';
import ArgonButton from '@/components/ArgonButton.vue';
import KeyboardShortcutsHelp from '@/components/KeyboardShortcutsHelp.vue';
import AutosaveIndicator from '@/components/AutosaveIndicator.vue';
import ProgressIndicator from '@/components/ProgressIndicator.vue';
import api from "@/services/api";
import { quillEditor } from 'vue3-quill';

const { proxy } = getCurrentInstance();

// Estado para loading y UI
const isSaving = ref(false);
const isSendingWhatsApp = ref(false);
const showPhysicalExam = ref(false);
const activeEditor = ref(null);

// Referencias
const medicineEditorRef = ref(null);
const indicationsEditorRef = ref(null);

const props = defineProps({
    notes: {
        type: Array,
        default: () => [],
    },
    noteForm: {
        type: Object,
        default: () => ({
            created: new Date().toISOString().split('T')[0]
        })
    },
    currentId: {
        type: Number,
        default: 0,
        required: true,
    },
    selectNote: {
        type: Function,
        required: true,
    },
    errors: {
        type: Object,
        default: () => ({}),
    },
    patientRecord: {
        type: Object,
        required: true
    },
    activeTabPrefix: {
        type: String,
        required: true
    }
});

const emit = defineEmits({
    'save-note': null,
    'reset-form': null,
    'update:currentId': null,
    'delete-note': null,
    'update:activeTab': null,
    'clear-field-error': null,
    'clear-all-errors': null
});

const {
    localNoteForm,
    filteredNotes,
    handleSubmit: originalHandleSubmit,
    selectNoteForm,
    deleteNote,
    handleDuplicateNote,
    formatDate,
    isEditing
} = useNoteFormGlobal(props, emit, props.activeTabPrefix);

// Wrapper para handleSubmit
const handleSubmit = async () => {
    await originalHandleSubmit();
};

// Asegúrate de que localNoteForm.medicines_txt esté definido
localNoteForm.medicines_txt = localNoteForm.medicines_txt || '';

// Funciones helper
const getTextLength = (html) => {
    if (!html) return 0;
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.textContent.trim().length;
};

const getWordCount = (html) => {
    if (!html) return 0;
    const temp = document.createElement('div');
    temp.innerHTML = html;
    const text = temp.textContent.trim();
    return text ? text.split(/\s+/).length : 0;
};

const getErrorMessage = (error) => {
    if (!error) return '';
    if (Array.isArray(error)) return error[0];
    return error;
};

// ⌨️ Configurar atajos de teclado
const { isMac } = useKeyboardShortcuts({
    onSave: handleSubmit,
    onNew: () => {
        emit('reset-form');
        emit('update:activeTab', 'create');
    },
    onList: () => {
        emit('update:activeTab', 'list');
    },
    onCancel: () => {
        if (props.currentId && props.currentId > 0) {
            emit('update:activeTab', 'view');
        }
    }
});

// 💾 Configurar auto-guardado
const autosaveKey = computed(() => `prescription-draft-${props.patientRecord?.id || 'temp'}`);
const fieldsToAutosave = [
    'medicines_txt',
    'indications_txt',
    'diagnosis_cie10_1',
    'appointment',
    'created'
];

const {
    lastSaveTime,
    hasUnsavedChanges,
    hasDraft,
    loadDraft,
    discardDraft,
    getFormattedSaveTime
} = useAutosave(
    localNoteForm,
    autosaveKey,
    30000, // 30 segundos
    fieldsToAutosave
);

// Restaurar borrador
const handleRestoreDraft = () => {
    const draft = loadDraft();
    if (draft) {
        Object.assign(localNoteForm.value, draft);
        console.log('✨ Borrador de receta restaurado exitosamente');
    }
};

// Descartar borrador
const handleDiscardDraft = () => {
    discardDraft();
    console.log('🗑️ Borrador de receta descartado');
};

// Watch para limpiar errores
watch(
    () => localNoteForm.value.medicines_txt,
    (newValue) => {
        if (newValue && props.errors.medicines_txt) {
            emit('clear-field-error', 'medicines_txt');
        }
    }
);

watch(
    () => localNoteForm.value.indications_txt,
    (newValue) => {
        if (newValue && props.errors.indications_txt) {
            emit('clear-field-error', 'indications_txt');
        }
    }
);

watch(
    () => localNoteForm.value.created,
    (newValue) => {
        if (newValue && props.errors.created) {
            emit('clear-field-error', 'created');
        }
    }
);

// Función para toggle de exploración física
const togglePhysicalExam = () => {
    showPhysicalExam.value = !showPhysicalExam.value;
};

// Generar PDF
const generatePDF = async () => {
    try {
        const formData = {
            prescription: JSON.stringify({
                id: String(localNoteForm.value.id),
                doctor: String(localNoteForm.value.doctor_id)
            })
        };

        const response = await api.post(`generate-labeled-pdf`, formData);

        if (response && response.status === 200) {
            window.open(response.data.data.pdf_url, '_blank');
            proxy.$notify(response.data.message, "", "success", "ni ni-like-2 ni-lg");
        } else {
            proxy.$notify(response.data.message, "", "danger", "ni ni-fat-remove ni-lg");
        }
    } catch (error) {
        if (error.response && error.response.data && error.response.data.message) {
            proxy.$notify(error.response.data.message, "", "danger", "ni ni-fat-remove ni-lg");
        } else {
            console.error('Ocurrió un error desconocido al generar generate-labeled-pdf.');
        }
    }
};

// Enviar por WhatsApp
const sendWhatsApp = async () => {
    try {
        isSendingWhatsApp.value = true;
        
        const response = await api.post('whatsapp/send-prescription-pdf', {
            prescription_id: localNoteForm.value.id
        });

        if (response && response.status === 200 && response.data.success) {
            const { patient_name, whatsapp } = response.data.data;
            proxy.$notify(
                response.data.message || `Receta enviada a ${patient_name} (${whatsapp})`, 
                "", 
                "success", 
                "fab fa-whatsapp ni-lg"
            );
        } else {
            proxy.$notify(
                response.data.message || "Error al enviar receta por WhatsApp", 
                "", 
                "danger", 
                "ni ni-fat-remove ni-lg"
            );
        }
    } catch (error) {
        if (error.response && error.response.data && error.response.data.message) {
            proxy.$notify(error.response.data.message, "", "danger", "ni ni-fat-remove ni-lg");
        } else {
            console.error('Ocurrió un error al enviar la receta por WhatsApp:', error);
            proxy.$notify(
                "Error al enviar receta por WhatsApp", 
                "", 
                "danger", 
                "ni ni-fat-remove ni-lg"
            );
        }
    } finally {
        isSendingWhatsApp.value = false;
    }
};

function addMedicineToEditor(medicine) {
    const { name, description } = medicine;
    const editor = medicineEditorRef.value?.__quill || medicineEditorRef.value?.$el?.__quill;

    if (!editor) {
        console.error("El editor Quill no está inicializado.");
        return;
    }

    const editorContent = editor.root.innerHTML;
    const medicineNumberRegex = /<strong>(\d+)\./g;
    let lastNumber = 0;
    let match;

    while ((match = medicineNumberRegex.exec(editorContent)) !== null) {
        lastNumber = Math.max(lastNumber, parseInt(match[1], 10));
    }

    const nextNumber = lastNumber + 1;

    // Limpiar descripción si parece duplicada (ej: ".- NOMBRE: descripción")
    let cleanDescription = description;
    const nameRegex = new RegExp(`^(\\.-)?\\s*${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*[:]?\\s*`, 'i');
    if (nameRegex.test(cleanDescription)) {
        cleanDescription = cleanDescription.replace(nameRegex, '').trim();
    }

    const newContent = `<strong>${nextNumber}.- ${name}</strong><br>&nbsp; &nbsp; &nbsp; ${cleanDescription} <br><br>`;

    // Si es el primer elemento y el editor está vacío, limpiar y pegar en índice 0
    const isEditorEmpty = editor.getText().trim().length === 0;

    if (nextNumber === 1 && isEditorEmpty) {
        editor.setContents([]); // Limpiar cualquier salto de línea o espacio fantasma
        editor.clipboard.dangerouslyPasteHTML(0, newContent);
    } else {
        editor.clipboard.dangerouslyPasteHTML(editor.getLength(), newContent);
    }

    if (localNoteForm.value) {
        localNoteForm.value.medicines_txt = editor.root.innerHTML;
    }
    proxy.$notify(`${name} agregado`, "", "success", "ni ni-like-2 ni-lg");
}

function addTemplateContentToEditor(template) {
    const { name, content } = template;
    const editor = medicineEditorRef.value?.__quill || medicineEditorRef.value?.$el?.__quill;

    if (editor) {
        const newContent = `<p style="text-align: center;"><strong>${name}</strong><br>${content}</p>`;
        editor.root.innerHTML = newContent;
        localNoteForm.medicines_txt = editor.root.innerHTML;
        proxy.$notify(`${name} agregado`, "", "success", "ni ni-like-2 ni-lg");
    } else {
        console.error("El editor Quill no está inicializado.");
    }
}

function updateEditorContent(content) {
    localNoteForm.medicines_txt = content;
}

onMounted(async () => {
    // No hacer scroll aquí - el MenuBar maneja el scroll cuando es necesario

    await nextTick();
    const editorMedicines = medicineEditorRef.value?.__quill || medicineEditorRef.value?.$el?.__quill;
    const editorIndications = indicationsEditorRef.value?.__quill || indicationsEditorRef.value?.$el?.__quill;

    if (editorMedicines) {
        localNoteForm.medicines_txt = editorMedicines.root.innerHTML;
    } else {
        console.error("El editor Quill de medicamentos no está inicializado.");
    }

    if (editorIndications) {
        localNoteForm.indications_txt = editorIndications.root.innerHTML;
    } else {
        console.error("El editor Quill de indicaciones no está inicializado.");
    }
});

// Watch para cuando cambie la nota (sin scroll - MenuBar lo maneja)
watch(() => props.note, () => {
    if (props.note) {
        // No hacer scroll aquí
    }
});
</script>

<style scoped>
.modern-form-container {
    --accent: #3b82f6;
    --accent-strong: #2563eb;
    --accent-soft: #eff6ff;
    --border: #f1f5f9;
    --surface: #ffffff;
    --muted-surface: #f8fafc;
    --text: #334155;
    --muted-text: #64748b;
    --shadow-sm: 0 1px 2px 0 rgb(0 0 0 / 0.05);
    --shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1);
    margin-bottom: 24px;
    background: var(--surface);
    border-radius: 16px;
    border: 1px solid var(--border);
    box-shadow: var(--shadow);
    overflow: hidden;
}

.form-header {
    background: white;
    border-bottom: 1px solid var(--border);
    padding: 1.5rem;
}

.header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
}

.title-section {
    display: flex;
    align-items: center;
    gap: 1rem;
}

.icon-wrapper {
    background: var(--accent-soft);
    border-radius: 12px;
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.25rem;
    color: var(--accent);
}

.form-title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text);
    letter-spacing: -0.025em;
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.id-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    background: var(--muted-surface);
    border: 1px solid var(--border);
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--muted-text);
}

.form-subtitle {
    margin: 0.25rem 0 0;
    color: var(--muted-text);
    font-size: 0.875rem;
}

.action-buttons {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.header-btn-primary {
    box-shadow: var(--shadow-sm) !important;
    font-weight: 500 !important;
    font-size: 0.875rem !important;
    padding: 0.625rem 1.25rem !important;
    border-radius: 8px !important;
    text-transform: none !important;
    background: var(--accent) !important;
    border: 1px solid var(--accent-strong) !important;
    color: white !important;
    transition: all 0.2s ease !important;
}

.header-btn-primary:hover {
    background: var(--accent-strong) !important;
    transform: translateY(-1px) !important;
}

.header-btn-secondary {
    box-shadow: none !important;
    padding: 0.5rem !important;
    border-radius: 8px !important;
    background: white !important;
    border: 1px solid var(--border) !important;
    color: var(--muted-text) !important;
    transition: all 0.2s ease !important;
}

.header-btn-secondary:hover {
    border-color: var(--accent) !important;
    color: var(--accent) !important;
    background: var(--accent-soft) !important;
}

.secondary-actions {
    display: flex;
    gap: 0.5rem;
    align-items: center;
}

.modern-form {
    background: var(--surface);
    padding: 1.5rem;
}

.status-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 1.5rem;
    background: var(--muted-surface);
    border-bottom: 1px solid var(--border);
    gap: 1rem;
}

.draft-badge {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.375rem 0.75rem;
    background: white;
    border: 1px solid var(--border);
    border-radius: 9999px;
    color: var(--muted-text);
    font-size: 0.75rem;
    font-weight: 500;
}

.draft-badge i {
    color: var(--accent);
}

.draft-btn-restore,
.draft-btn-discard {
    padding: 0.25rem;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.75rem;
    transition: all 0.2s ease;
    border: none;
    background: transparent;
    color: var(--muted-text);
}

.draft-btn-restore:hover {
    color: var(--accent);
    background: var(--accent-soft);
}

.draft-btn-discard:hover {
    color: #ef4444;
    background: #fee2e2;
}

.keyboard-help-btn {
    padding: 0.5rem;
    background: white;
    border: 1px solid var(--border);
    border-radius: 8px;
    color: var(--muted-text);
    cursor: pointer;
    font-size: 0.875rem;
    transition: all 0.2s ease;
}

.keyboard-help-btn:hover {
    border-color: var(--accent);
    color: var(--accent);
}

.tools-section {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1rem;
    margin-bottom: 2rem;
}

.tool-card {
    background: white;
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 1rem;
    transition: all 0.2s ease;
}

.tool-card:hover {
    border-color: var(--accent);
    box-shadow: var(--shadow-sm);
}

.form-section {
    background: white;
    border-radius: 12px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
    border: 1px solid var(--border);
    box-shadow: var(--shadow-sm);
}

.section-title {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1.25rem;
    font-weight: 600;
    color: var(--text);
    font-size: 1rem;
}

.section-title i {
    color: var(--accent);
    font-size: 1.1rem;
}

.section-title.enhanced {
    background: transparent;
    border: none;
    border-bottom: 1px solid var(--border);
    padding: 0 0 1rem 0;
    margin: 0 0 1.5rem 0;
    border-radius: 0;
}

.section-title.collapsible {
    cursor: pointer;
    user-select: none;
    margin-bottom: 0;
    justify-content: space-between;
}

.toggle-icon {
    transition: transform 0.2s ease;
    color: var(--muted-text);
    font-size: 0.875rem;
}

.toggle-icon.rotated {
    transform: rotate(-180deg);
}

.form-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
    font-weight: 500;
    color: var(--text);
    font-size: 0.875rem;
}

.form-label.modern {
    justify-content: space-between;
    margin-bottom: 0.75rem;
}

.label-content {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
}

.label-content i {
    color: var(--muted-text);
    font-size: 0.875rem;
}

.required-badge {
    background: var(--accent-soft);
    color: var(--accent);
    font-size: 0.7rem;
    padding: 0.125rem 0.5rem;
    border-radius: 9999px;
    font-weight: 600;
}

.editor-wrapper {
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid var(--border);
    background: white;
    transition: all 0.2s ease;
}

.editor-wrapper:focus-within {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px var(--accent-soft);
}

.editor-wrapper.has-error {
    border-color: #ef4444;
}

.error-message-modern {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.5rem;
    color: #ef4444;
    font-size: 0.875rem;
}

.help-text-modern {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.5rem;
    color: var(--muted-text);
    font-size: 0.875rem;
}

:deep(.ql-toolbar) {
    background: var(--muted-surface);
    border: none;
    border-bottom: 1px solid var(--border);
    padding: 0.75rem;
}

:deep(.ql-container) {
    border: none !important;
    font-size: 0.95rem;
    font-family: inherit;
}

:deep(.ql-editor) {
    min-height: 150px;
    padding: 1rem;
}

.editor-footer {
    background: var(--muted-surface);
    border-top: 1px solid var(--border);
    padding: 0.5rem 1rem;
}

.editor-stats {
    display: flex;
    gap: 1rem;
}

.stat-item {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    font-size: 0.75rem;
    color: var(--muted-text);
}

.diagnosis-full,
.date-item {
    background: var(--muted-surface);
    padding: 1.25rem;
    border-radius: 8px;
    border: 1px solid var(--border);
}

.dates-row {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.25rem;
    margin-top: 1.25rem;
}

.collapsible-content {
    border-top: 1px solid var(--border);
    padding-top: 1.5rem;
    margin-top: 1rem;
}

.notes-navigation {
    background: var(--muted-surface);
    border-radius: 12px;
    padding: 1rem;
    margin-top: 1.5rem;
    border: 1px solid var(--border);
}

.status-right {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

@media (max-width: 768px) {
    .status-bar {
        flex-direction: column;
        gap: 1rem;
        padding: 1rem;
    }

    .status-left,
    .status-center,
    .status-right {
        width: 100%;
        justify-content: center;
    }

    .header-content {
        flex-direction: column;
        text-align: center;
    }

    .modern-form {
        padding: 1rem;
    }

    .tools-section {
        grid-template-columns: 1fr;
    }

    .dates-row {
        grid-template-columns: 1fr;
    }
}
</style>
